export function normalizeCompanyId(value) {
    const normalized = Number(value);
    return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
}

export function normalizeCompanyIds(value) {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((companyId) => normalizeCompanyId(companyId))
        .filter((companyId) => companyId !== null)
        .filter((companyId, index, companyIds) => companyIds.indexOf(companyId) === index);
}

export function companyMatchesRole(company, roleGroup) {
    if (!company || typeof company !== 'object') {
        return false;
    }

    if (roleGroup === 'customer') {
        return company.is_customer_company === true;
    }

    if (roleGroup === 'service_provider') {
        return company.is_provider_company === true;
    }

    return false;
}

export function normalizeCompanyAssignment(assignment) {
    const companyId = normalizeCompanyId(assignment && assignment.company_id);
    if (!companyId) {
        return null;
    }

    return {company_id: companyId};
}

export function normalizeCompanyAssignments(assignments, fallbackCompanyIds = []) {
    const assignmentByCompanyId = new Map();

    if (Array.isArray(assignments)) {
        assignments.forEach((assignment) => {
            const normalized = normalizeCompanyAssignment(assignment);
            if (normalized) {
                assignmentByCompanyId.set(normalized.company_id, normalized);
            }
        });
    }

    normalizeCompanyIds(fallbackCompanyIds).forEach((companyId) => {
        if (!assignmentByCompanyId.has(companyId)) {
            assignmentByCompanyId.set(companyId, normalizeCompanyAssignment({company_id: companyId}));
        }
    });

    return Array.from(assignmentByCompanyId.values());
}

export function normalizeUserCompanyAssignment(
    roleGroup,
    companyId,
    companyIds,
    companies = [],
    companyAssignments = []
) {
    if (!['customer', 'service_provider'].includes(roleGroup)) {
        return {companyId: null, companyIds: [], companyAssignments: []};
    }

    const eligibleCompanyIds = Array.isArray(companies) && companies.length > 0
        ? new Set(
            companies
                .filter((company) => companyMatchesRole(company, roleGroup))
                .map((company) => normalizeCompanyId(company.id))
                .filter((id) => id !== null)
        )
        : null;
    const isEligible = (id) => id !== null && (!eligibleCompanyIds || eligibleCompanyIds.has(id));
    const normalizedCompanyId = normalizeCompanyId(companyId);
    const normalizedCompanyIds = normalizeCompanyIds(companyIds).filter(isEligible);

    if (roleGroup === 'service_provider') {
        const providerCompanyId = isEligible(normalizedCompanyId)
            ? normalizedCompanyId
            : (normalizedCompanyIds[0] || null);

        return {
            companyId: providerCompanyId,
            companyIds: providerCompanyId ? [providerCompanyId] : [],
            companyAssignments: [],
        };
    }

    const hasExplicitCompanySelection = normalizedCompanyIds.length > 0
        || (Array.isArray(companyAssignments) && companyAssignments.length > 0);
    const legacyFallbackIds = hasExplicitCompanySelection
        ? normalizedCompanyIds
        : (isEligible(normalizedCompanyId) ? [normalizedCompanyId] : []);
    const normalizedAssignments = normalizeCompanyAssignments(
        companyAssignments,
        legacyFallbackIds
    ).filter((assignment) => isEligible(assignment.company_id));
    const selectedCompanyIds = normalizedAssignments.map((assignment) => assignment.company_id);
    const primaryCompanyId = normalizedCompanyId !== null && selectedCompanyIds.includes(normalizedCompanyId)
        ? normalizedCompanyId
        : (selectedCompanyIds[0] || null);

    return {
        companyId: primaryCompanyId,
        companyIds: selectedCompanyIds,
        companyAssignments: normalizedAssignments,
    };
}
